import { UseS3ServiceReturn } from '../../../drive/useS3Service';
import { useDeleteOperations } from './useDeleteOperations';
import { useDirectoryOperations } from './useDirectoryOperations';
import { useFileHandling } from './useFileHandling';
import { useFileOperations } from './useFileOperations';
import { useOperationErrors } from './useOperationErrors';
import { useRenameOperations } from './useRenameOperations';

export interface UseDriveOperationsProps {
  s3Service: UseS3ServiceReturn;
  currentDirectoryPath: string;
  stsDriveUrl?: string;
  workspaceDriveUrl?: string;
  onFileUploadStart?: (key: string, filename: string) => void;
  onFileUploadComplete?: (key: string) => void;
}

export const useDriveOperations = (props: UseDriveOperationsProps) => {
  const { s3Service, currentDirectoryPath, stsDriveUrl, workspaceDriveUrl, onFileUploadStart, onFileUploadComplete } =
    props;
  const operationErrors = useOperationErrors();
  const fileHandling = useFileHandling({
    s3Service,
    currentDirectoryPath,
    onFileUploadStart,
    onFileUploadComplete,
  });
  const directoryOperations = useDirectoryOperations({ s3Service, currentDirectoryPath });
  const deleteOperations = useDeleteOperations({ handleDeleteFile: fileHandling.handleDeleteFile });
  const fileOperations = useFileOperations({
    s3Service,
    stsDriveUrl,
    workspaceDriveUrl,
    onOperationComplete: () => {
      // Refresh objects after copy/move operation completes
      if (s3Service.isConnected) s3Service.listObjects(currentDirectoryPath);
    },
    onOperationError: operationErrors.setError,
  });
  const renameOperations = useRenameOperations({ s3Service, currentDirectoryPath });

  return {
    validationModalOpen: fileHandling.validationModalOpen,
    invalidFileResults: fileHandling.invalidFileResults,
    handleFileUpload: fileHandling.handleFileUpload,
    handleValidationModalClose: fileHandling.handleValidationModalClose,
    handleValidationModalConfirm: fileHandling.handleValidationModalConfirm,
    isUploadingFiles: fileHandling.isUploadingFiles,

    directoryModalOpen: directoryOperations.directoryModalOpen,
    existingDirectories: directoryOperations.existingDirectories,
    handleCreateDirectory: directoryOperations.handleCreateDirectory,
    handleDirectoryModalClose: directoryOperations.handleDirectoryModalClose,
    handleDirectoryModalConfirm: directoryOperations.handleDirectoryModalConfirm,

    deleteConfirmationOpen: deleteOperations.deleteConfirmationOpen,
    itemToDelete: deleteOperations.itemToDelete,
    handleDeleteRequest: deleteOperations.handleDeleteRequest,
    handleDeleteConfirmationClose: deleteOperations.handleDeleteConfirmationClose,
    handleDeleteConfirmationConfirm: deleteOperations.handleDeleteConfirmationConfirm,

    destinationModalOpen: fileOperations.destinationModalOpen,
    copyMoveOperation: fileOperations.copyMoveOperation,
    allWorkspaceObjects: fileOperations.allWorkspaceObjects,
    isOperationInProgress: fileOperations.isOperationInProgress,
    handleCopyFile: fileOperations.handleCopyFile,
    handleMoveFile: fileOperations.handleMoveFile,
    handleDownloadFile: fileOperations.handleDownloadFile,
    handleDestinationModalClose: fileOperations.handleDestinationModalClose,
    handleDestinationModalConfirm: fileOperations.handleDestinationModalConfirm,
    refreshAllObjects: fileOperations.refreshAllObjects,

    renameModalOpen: renameOperations.renameModalOpen,
    itemToRename: renameOperations.itemToRename,
    handleRenameRequest: renameOperations.handleRenameRequest,
    handleRenameModalClose: renameOperations.handleRenameModalClose,
    handleRenameModalConfirm: renameOperations.handleRenameModalConfirm,
    generateUniqueName: renameOperations.generateUniqueName,

    operationErrors,
  };
};
